import { createElement, KeyboardEventHandler, MutableRefObject, useRef } from 'react';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { ReactFocusEvent, ReactKeyboardEvent } from '../../../react-synthetic-events';
import { BasicColorPickerProps, ColorChipType } from './types';
import { basicColors } from './BasicColorPallete';
import { ColorChips } from './ColorChips';
import { Flow } from '../../Layouts/Flow';
import { Label } from '../../Label';
import { ColorIndicator } from './ColorIndicator';
import { postfixTestId } from '../../../util/testId';
import { useIsRTL } from '../../../locale';
import { EMPTY_CHIP_COLOR, recentStandardColors } from '.';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';

import cn from './BasicColorPicker.css.json';
import './BasicColorPicker.css';

const useHtmlIds = createUseHtmlId('BasicColorPicker');

export const BasicColorPicker = (props: BasicColorPickerProps) => {
  const localize = useNCTextLocalizer('ColorPicker');
  const standardLabel = localize('standard.label');
  const automaticLabel = localize('automatic.label');
  const nocolorLabel = localize('noColor.label');
  const recentCustomLabel = localize('recentCustom.label');
  const recentStandardLabel = localize('recentStandard.label');
  const isRTL = useIsRTL();
  const numberOfRecentCustomRows = Math.max(0, Math.min(props.numberOfRecentCustomRows ?? 1, 3));
  const recentCustomColorBlocks = [
    ...props.recentCustomColors,
    ...Array(numberOfRecentCustomRows * 7).fill(EMPTY_CHIP_COLOR),
  ].slice(0, numberOfRecentCustomRows * 7);

  const selectedColor = props.newColor;
  const automaticColor = props.automaticColor ?? '#000000';

  const isEmptyChip = (chip: HTMLElement) => {
    return (chip.firstElementChild as HTMLElement).style.backgroundColor === EMPTY_CHIP_COLOR;
  };

  const changeFocus = (focusedElement: HTMLElement, elementToFocus: HTMLElement) => {
    focusedElement.tabIndex = -1;
    elementToFocus.tabIndex = 0;
    elementToFocus.focus();
  };

  const onRecentStandardKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const eventTarget = e.target as HTMLElement;
    let preventDefault = true;
    switch (e.key) {
      case 'ArrowUp': /* falls through */
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (eventTarget.previousElementSibling) {
          const previous = eventTarget.previousElementSibling as HTMLElement;
          if (isEmptyChip(previous)) break;
          changeFocus(eventTarget, previous);
        }
        break;
      case 'ArrowDown': /* falls through */
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (eventTarget.nextElementSibling) {
          const next = eventTarget.nextElementSibling as HTMLElement;
          if (isEmptyChip(next)) break;
          changeFocus(eventTarget, next);
        }
        break;
      case 'Home':
        if (eventTarget.parentElement) {
          const children = [...eventTarget.parentElement.childNodes];
          changeFocus(eventTarget, children[0] as HTMLElement);
        }
        break;
      case 'End':
        if (eventTarget.parentElement) {
          const lastColorIndex =
            recentStandardColors.filter((color) => color !== EMPTY_CHIP_COLOR).length - 1;
          const children = [...eventTarget.parentElement.childNodes];
          changeFocus(eventTarget, children[lastColorIndex] as HTMLElement);
        }
        break;

      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onRecentCustomKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (numberOfRecentCustomRows === 1 && e.key !== 'End' && e.key !== 'Home') {
      onRecentStandardKeyDown(e);
    } else {
      let preventDefault = true;
      const eventTarget = e.target as HTMLElement;
      const colorCount = props.recentCustomColors.length;
      const columnCount = 7;
      switch (e.key) {
        case isRTL ? 'ArrowRight' : 'ArrowLeft':
          if (eventTarget.previousElementSibling) {
            const previous = eventTarget.previousElementSibling as HTMLElement;
            if (isEmptyChip(previous)) break;
            changeFocus(eventTarget, previous);
          }
          break;
        case isRTL ? 'ArrowLeft' : 'ArrowRight':
          if (eventTarget.nextElementSibling) {
            const next = eventTarget.nextElementSibling as HTMLElement;
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'ArrowUp':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            const currentIndex = children.indexOf(eventTarget);
            if (currentIndex === 0) break;

            const currentRow = Math.floor(currentIndex / columnCount);
            const currentColumn = currentIndex % columnCount;

            const lastChipRow = Math.floor((colorCount - 1) / columnCount);
            const lastChipColumn = (colorCount - 1) % columnCount;

            const nextIndex =
              currentRow === 0
                ? currentColumn > lastChipColumn + 1
                  ? currentIndex + (lastChipRow - 1) * columnCount - 1
                  : currentIndex + lastChipRow * columnCount - 1
                : currentIndex - columnCount;

            const next = children[nextIndex] as HTMLElement;
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'ArrowDown':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            const currentIndex = children.indexOf(eventTarget);
            if (currentIndex === colorCount - (colorCount % columnCount) - 1) break;

            const currentColumn = currentIndex % columnCount;
            const nextIndex =
              currentIndex < colorCount - columnCount
                ? currentIndex + columnCount
                : currentColumn + 1;

            const next = children[nextIndex] as HTMLElement;
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'Home':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            changeFocus(eventTarget, children[0] as HTMLElement);
          }
          break;

        case 'End':
          if (eventTarget.parentElement) {
            const lastColorIndex =
              numberOfRecentCustomRows * columnCount >= colorCount
                ? colorCount - 1
                : numberOfRecentCustomRows * columnCount - 1;
            const children = [...eventTarget.parentElement.childNodes];
            changeFocus(eventTarget, children[lastColorIndex] as HTMLElement);
          }
          break;
        default:
          preventDefault = false;
          break;
      }
      if (preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleColorChipPress = (color: string, type: ColorChipType) => {
    props.onColorSelect?.({ color, type });
  };

  const handleonkeydown: KeyboardEventHandler = (event) => {
    let preventDefault = true;
    const eventTarget = event.target as HTMLElement;
    const rowCount = 5;
    switch (event.key) {
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        {
          if (eventTarget.parentNode) {
            const children = eventTarget.parentNode.childNodes;
            const currentIndex = [...children].indexOf(eventTarget);
            if (currentIndex > 0) {
              const nextIndex =
                currentIndex >= rowCount
                  ? currentIndex - rowCount
                  : children.length - 1 - rowCount + currentIndex;
              const next = children[nextIndex] as HTMLElement;
              changeFocus(eventTarget, next);
            }
          }
        }
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        {
          if (eventTarget.parentNode) {
            const children = eventTarget.parentNode.childNodes;
            const currentIndex = [...children].indexOf(eventTarget);
            if (currentIndex < children.length - 1) {
              const nextIndex =
                currentIndex < children.length - rowCount
                  ? currentIndex + rowCount
                  : rowCount - (children.length - 1 - currentIndex);
              const next = children[nextIndex] as HTMLElement;
              changeFocus(eventTarget, next);
            }
          }
        }
        break;
      case 'ArrowUp':
        {
          if (eventTarget.previousElementSibling) {
            const next = eventTarget.previousElementSibling as HTMLElement;
            changeFocus(eventTarget, next);
          }
        }
        break;
      case 'ArrowDown':
        {
          if (eventTarget.nextElementSibling) {
            const next: HTMLElement = eventTarget.nextElementSibling as HTMLElement;
            changeFocus(eventTarget, next);
          }
        }
        break;
      case 'Home':
        if (eventTarget.parentNode?.firstElementChild) {
          const next = eventTarget.parentNode.firstElementChild as HTMLElement;
          changeFocus(eventTarget, next);
        }
        break;
      case 'End':
        if (eventTarget.parentNode?.lastElementChild) {
          const next = eventTarget.parentNode.lastElementChild as HTMLElement;
          changeFocus(eventTarget, next);
        }
        break;
      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const [standardColorsId, recentCustomColorsId, recentStandardColorsId] = useHtmlIds(3);
  const directSpeech = useDirectSpeech();
  const isStandardTextAnnounced = useRef(false);
  const isRecentCustomTextAnnounced = useRef(false);
  const isRecentStandardTextAnnounced = useRef(false);

  const handleOnFocus = (text: string, isTextAnnounced: MutableRefObject<boolean>) => {
    if (!isTextAnnounced.current) {
      directSpeech?.notify(text);
      isTextAnnounced.current = true;
    }
  };

  const handleOnBlur = (
    e: ReactFocusEvent<HTMLDivElement>,
    isTextAnnounced: MutableRefObject<boolean>
  ) => {
    if (isTextAnnounced.current && !e.currentTarget.contains(e.relatedTarget)) {
      isTextAnnounced.current = false;
    }
  };

  return (
    <Flow
      id={props.id}
      role="tabpanel"
      orientation="vertical"
      className={cn.container}
      data-testid={postfixTestId(props.testId, 'container')}
    >
      {props.showColorIndicator && (
        <ColorIndicator currentColor={props.selectedColor} newColor={props.newColor} />
      )}
      <Flow orientation="vertical" spacing="s" className={cn.basicColorContainer}>
        <Flow orientation="vertical" spacing="xs">
          <Label id={standardColorsId} className={cn.label}>
            {standardLabel}
          </Label>
          <Flow
            role="toolbar"
            aria-labelledby={standardColorsId}
            className={cn.standardColorChips}
            onKeyDown={handleonkeydown}
            spacing="none"
            onFocus={() => {
              handleOnFocus(localize('basicTab.standardColors.msg'), isStandardTextAnnounced);
            }}
            onBlur={(e) => {
              handleOnBlur(e, isStandardTextAnnounced);
            }}
          >
            {basicColors.map((standardColor) => {
              return (
                <ColorChips
                  key={standardColor}
                  {...props}
                  color={standardColor}
                  type="standard"
                  selected={
                    (standardColor === selectedColor?.color &&
                      selectedColor?.type === 'standard') ||
                    (standardColor === '#000000' && selectedColor?.type !== 'standard')
                  }
                  tabToStandardBlock={
                    standardColor === '#000000' && selectedColor?.type !== 'standard'
                  }
                  onPress={handleColorChipPress}
                  testId={postfixTestId(props.testId, 'standardColorChip')}
                />
              );
            })}
          </Flow>
        </Flow>
        {numberOfRecentCustomRows !== 0 && (
          <Flow orientation="vertical" spacing="xs">
            <Label id={recentCustomColorsId} className={cn.label}>
              {recentCustomLabel}
            </Label>
            <Flow
              role="toolbar"
              aria-labelledby={recentCustomColorsId}
              className={cn.recentCustomChips}
              onKeyDown={onRecentCustomKeyDown}
              onFocus={() => {
                handleOnFocus(
                  localize('basicTab.recentCustomColors.msg'),
                  isRecentCustomTextAnnounced
                );
              }}
              onBlur={(e) => {
                handleOnBlur(e, isRecentCustomTextAnnounced);
              }}
            >
              {recentCustomColorBlocks.map((color, i) => {
                return (
                  <ColorChips
                    key={`recent-custom-${i}`}
                    testId={postfixTestId(props.testId, `recentCustomColorChip-${i}`)}
                    color={color}
                    type="custom"
                    onPress={handleColorChipPress}
                    focusable={i === 0 && color !== EMPTY_CHIP_COLOR}
                  />
                );
              })}
            </Flow>
          </Flow>
        )}
        <Flow orientation="vertical" spacing="xs">
          <Label id={recentStandardColorsId} className={cn.label}>
            {recentStandardLabel}
          </Label>
          <Flow
            role="toolbar"
            aria-labelledby={recentStandardColorsId}
            className={cn.recentStandardChips}
            onKeyDown={onRecentStandardKeyDown}
            onFocus={() => {
              handleOnFocus(
                localize('basicTab.recentStandardColors.msg'),
                isRecentStandardTextAnnounced
              );
            }}
            onBlur={(e) => {
              handleOnBlur(e, isRecentStandardTextAnnounced);
            }}
          >
            {recentStandardColors.map((color, i) => {
              return (
                <ColorChips
                  key={`recent-standard-${i}`}
                  testId={postfixTestId(props.testId, `recentStandardColorChip-${i}`)}
                  color={color}
                  type="standard"
                  onPress={handleColorChipPress}
                  focusable={i === 0 && color !== EMPTY_CHIP_COLOR}
                />
              );
            })}
          </Flow>
        </Flow>
        <Flow
          spacing="s"
          orientation="vertical"
          className={cn.defultColorSelection}
          onKeyDown={(e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
              e.stopPropagation();
              e.preventDefault();
            }
          }}
        >
          {!props.hideAutomaticColor && (
            <Flow spacing="xs">
              <ColorChips
                type="automatic"
                {...props}
                color={automaticColor}
                selected={
                  automaticColor.toUpperCase() === selectedColor?.color.toUpperCase() &&
                  selectedColor?.type === 'automatic'
                }
                onPress={handleColorChipPress}
                testId={postfixTestId(props.testId, 'automaticColorChip')}
              />
              <Label>{automaticLabel}</Label>
            </Flow>
          )}
          {!props.hideNoColor ? (
            <Flow spacing="xs">
              <ColorChips
                type="nocolor"
                {...props}
                color=""
                selected={selectedColor?.color === '' && selectedColor?.type === 'nocolor'}
                onPress={handleColorChipPress}
                testId={postfixTestId(props.testId, 'nocolorColorChip')}
              />
              <Label>{nocolorLabel}</Label>
            </Flow>
          ) : null}
        </Flow>
      </Flow>
    </Flow>
  );
};
